import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));

  useEffect(() => {
    verifyToken();
  }, []);

  const verifyToken = async () => {
    const storedToken = localStorage.getItem('auth_token');
    if (!storedToken) {
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }

    try {
      await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setIsAuthenticated(true);
      setToken(storedToken);
    } catch (error) {
      localStorage.removeItem('auth_token');
      setIsAuthenticated(false);
      setToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { password });
      const { access_token } = response.data;
      localStorage.setItem('auth_token', access_token);
      setToken(access_token);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setIsAuthenticated(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Password change failed' };
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, token, login, logout, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};
